import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { sounds } from "@/lib/sounds";
import GameCanvas from "@/components/GameCanvas";
import GameOverModal from "@/components/GameOverModal";
import Leaderboard from "@/components/Leaderboard";
import BottomNav from "@/components/BottomNav";
import HomeSeoSections from "@/components/HomeSeoSections";
import { LogOut, Award, Zap, Play, User, Save, X, Trophy, Share2, TrendingDown, Crown, Sparkles, Swords } from "lucide-react";
import WinnerCard from "@/components/WinnerCard";
import { toast } from "sonner";
import { signScore, getRecaptchaToken } from "@/lib/security";
import { useGeo } from "@/lib/geo";

const MAX_RESUMES = 3;
const LS_HOWTO = "dg_played_once";
const LS_HIGHSCORE_PREFIX = "dg_highscore_"; // per-user: dg_highscore_<user_id>
const BGM_BASE_RATE = 1.0;
const BGM_MAX_RATE = 1.8;

// Per-user localStorage key so each Google account has its own Personal Best.
const highScoreKey = (u) => (u?.user_id ? `${LS_HIGHSCORE_PREFIX}${u.user_id}` : "dg_highscore");
const readLocalBest = (u) => {
  if (typeof window === "undefined" || !u?.user_id) return 0;
  return parseInt(localStorage.getItem(highScoreKey(u)) || "0", 10) || 0;
};
const writeLocalBest = (u, score) => {
  if (typeof window === "undefined" || !u?.user_id) return;
  localStorage.setItem(highScoreKey(u), String(score));
};

export default function Game() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isIndia } = useGeo();
  const isGuestMode = !!location.state?.guestMode;
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user && !isGuestMode);
  const [isGuest, setIsGuest] = useState(isGuestMode);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [isNewHighScore, setIsNewHighScore] = useState(false);
  const [resumesUsed, setResumesUsed] = useState(0);
  const [inTop3, setInTop3] = useState(false);
  const [upiSaved, setUpiSaved] = useState(false);
  const [upiInput, setUpiInput] = useState("");
  const [showHowToPlay, setShowHowToPlay] = useState(
    typeof window !== "undefined" ? !localStorage.getItem(LS_HOWTO) : true
  );
  const [showProfile, setShowProfile] = useState(!!location.state?.openProfile);
  const [profileUpi, setProfileUpi] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  // Career stats fetched for the VIP own-profile modal:
  // { all_time_high, room_wins, leaderboard_first_finishes, current_daily_rank }
  const [careerStats, setCareerStats] = useState(null);
  const [careerLoading, setCareerLoading] = useState(false);
  // Winner card — if this user won any of the last 7 daily prizes
  const [myWinCard, setMyWinCard] = useState(null); // {winner, date, dayLabel} or null
  const [showWinCard, setShowWinCard] = useState(false);
  // Live top-3 challenge popup (fires when user newly enters/improves within top 3)
  const [liveTopCard, setLiveTopCard] = useState(null); // {rank, score} or null
  const [dailyBestScore, setDailyBestScore] = useState(0);
  const prevRankRef = useRef(null);

  // "Rank dropped since your last visit" popup (shown once per site-open)
  const [rankDropPopup, setRankDropPopup] = useState(null);
    // { previousRank: number|null, currentRank: number|null }
  const rankDropChecked = useRef(false);

  const canvasRef = useRef(null);
  const startTimeRef = useRef(null);
  const totalDurationRef = useRef(0);
  const bgmRateIntervalRef = useRef(null);
  // Anti-cheat: run-scoped session state (closure-local, never on window).
  const runSessionRef = useRef(null); // { nonce, clientStart, serverStart }
  const [liveScore, setLiveScore] = useState(0);
  // Personal Best for the LOGGED-IN player — mirrors server + localStorage.
  const [highScore, setHighScore] = useState(0);

  // Once we know the user, hydrate personal best from localStorage instantly.
  useEffect(() => {
    if (user?.user_id) setHighScore(readLocalBest(user));
  }, [user]);

  // Auth check — always refresh from /auth/me on mount so is_pro/plan reflect
  // the latest subscription state (e.g. right after DarePass activation on /pricing
  // or after Google re-login). Prevents stale "Resume with Ad" for DarePass users.
  // Guest mode: skip the auth call entirely and hydrate with an anonymous
  // placeholder user; the real identity is created when they submit a
  // username from the Game Over popup.
  useEffect(() => {
    if (isGuestMode) {
      setUser({
        user_id: null,
        name: "Guest",
        email: null,
        picture: null,
        is_guest: true,
        is_pro: false,
      });
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        setIsGuest(false);
      } catch {
        // No session — treat as anonymous guest so the game canvas is
        // playable immediately for first-time visitors. Real identity is
        // only created if they submit a username from the Game Over popup
        // or sign in with Google. This is the "game-first homepage" flow.
        setUser({
          user_id: null,
          name: "Guest",
          email: null,
          picture: null,
          is_guest: true,
          is_pro: false,
        });
        setIsGuest(true);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup sounds on unmount
  useEffect(() => {
    return () => {
      sounds.stopAll();
      if (bgmRateIntervalRef.current) clearInterval(bgmRateIntervalRef.current);
    };
  }, []);

  // Winner-card check: if the current user was #1 on any of the PRIOR days
  // (past 7 days, excluding today), stash for the "Share Past Win" prompt.
  // Fetch winner cards for the Share Win button (data only — no auto popup).
  // User feedback: the auto-popup was disruptive on every navigation. The "Share Win"
  // button in the header remains the sole entry point to the winner card.
  useEffect(() => {
    if (!user?.user_id) return;
    // Winner card / prize pool is India-only wording — skip the fetch entirely
    // for non-India users so we never render ₹500 PRIZE copy for them.
    if (!isIndia) return;
    (async () => {
      try {
        const res = await api.get("/winners/daily?days=7");
        const wins = (res.data.winners || []).filter(
          (w) => !w.is_today && w.winner && w.winner.user_id === user.user_id
        );
        if (wins.length === 0) return;
        const latest = wins[0];
        setMyWinCard({
          winner: latest.winner,
          date: latest.date,
          dayLabel: latest.day_label,
        });
      } catch {}
    })();
  }, [user?.user_id, isIndia]);

  const fetchRank = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const res = await api.get("/me/rank");
      const newRank = res.data.daily_rank || null; // 1|2|3|null
      const dailyBest = res.data.daily_best_score || 0;
      setDailyBestScore(dailyBest);

      setInTop3(!!res.data.in_top3);
      setUpiSaved(!!res.data.upi);
      if (res.data.upi) setUpiInput(res.data.upi);
      const serverBest = res.data.personal_best || 0;
      const localBest = readLocalBest(user);
      const best = Math.max(serverBest, localBest);
      setHighScore(best);
      writeLocalBest(user, best);

      // Congratulations popup: fire when rank IMPROVES into top-3.
      // Compare with the previous rank stored in a ref to avoid duplicate popups.
      // Suppressed on the very first fetchRank if a rank-DROP popup will fire —
      // showing both simultaneously is visually chaotic.
      const rankKey = `dd_last_rank_${user.user_id}`;
      const baseline = rankDropChecked.current
        ? null
        : localStorage.getItem(rankKey);
      const wouldFireDropPopup =
        baseline != null &&
        (newRank == null || newRank > parseInt(baseline, 10));
      if (
        isIndia &&
        newRank &&
        newRank >= 1 &&
        newRank <= 3 &&
        !wouldFireDropPopup
      ) {
        const prev = prevRankRef.current;
        if (prev == null || newRank < prev) {
          setLiveTopCard({
            rank: newRank,
            score: dailyBest,
          });
        }
      }
      prevRankRef.current = newRank;

      // One-time "you dropped since last visit" popup — fires ONCE per
      // site-open. Reads previous rank saved on last close from localStorage.
      // Only for users who WERE in top-100 previously.
      if (!rankDropChecked.current) {
        rankDropChecked.current = true;
        const rankKey = `dd_last_rank_${user.user_id}`;
        const rawPrev = localStorage.getItem(rankKey);
        const prevRank = rawPrev == null ? null : parseInt(rawPrev, 10);
        const cur = newRank; // null | 1..100
        const dropped =
          prevRank != null &&
          (cur == null ? true : cur > prevRank);
        if (dropped) {
          setRankDropPopup({
            previousRank: prevRank,
            currentRank: cur,
          });
        }
        // Save the current rank as the new baseline (only if in top-100)
        if (cur != null) localStorage.setItem(rankKey, String(cur));
        else localStorage.removeItem(rankKey);
      }
    } catch {}
  }, [user, isIndia]);

  useEffect(() => {
    if (user) fetchRank();
  }, [user, fetchRank]);

  // Save current rank to localStorage on tab close so next visit can compare.
  useEffect(() => {
    if (!user?.user_id) return;
    const handleUnload = () => {
      const rankKey = `dd_last_rank_${user.user_id}`;
      const cur = prevRankRef.current;
      if (cur != null) localStorage.setItem(rankKey, String(cur));
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      handleUnload();
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [user?.user_id]);

  // BGM speed + live score watcher
  const startBgmRateWatcher = () => {
    if (bgmRateIntervalRef.current) clearInterval(bgmRateIntervalRef.current);
    bgmRateIntervalRef.current = setInterval(() => {
      const score = canvasRef.current?.currentScore() || 0;
      setLiveScore(score);
      const rate = Math.min(BGM_MAX_RATE, BGM_BASE_RATE + score * 0.0008);
      sounds.setBgmSpeed(rate);
    }, 200);
  };
  const stopBgmRateWatcher = () => {
    if (bgmRateIntervalRef.current) {
      clearInterval(bgmRateIntervalRef.current);
      bgmRateIntervalRef.current = null;
    }
  };

  const startGame = async () => {
    sounds.play("click");
    sounds.setBgmSpeed(BGM_BASE_RATE);
    sounds.playBGM();
    startBgmRateWatcher();
    setGameOver(false);
    setIsNewHighScore(false);
    setResumesUsed(0);
    setLiveScore(0);
    totalDurationRef.current = 0;
    startTimeRef.current = performance.now();
    // Anti-cheat: request a server nonce before the run starts (skipped
    // for guests — they have no session yet; their first score submits
    // via the legacy path bundled into /api/auth/guest).
    if (!isGuest) {
      try {
        const res = await api.post("/game/start");
        runSessionRef.current = {
          nonce: res.data.nonce,
          clientStart: Date.now(),
          serverStart: res.data.server_time,
        };
      } catch {
        runSessionRef.current = null;
      }
    } else {
      runSessionRef.current = null;
    }
    setGameStarted(true);
    setTimeout(() => canvasRef.current?.start(), 30);
  };

  const handleGameOver = useCallback(
    async ({ score }) => {
      // Stop BGM immediately, play crash
      sounds.stopBGM();
      sounds.stopRain();
      stopBgmRateWatcher();
      sounds.play("crash");

      const now = performance.now();
      if (startTimeRef.current) {
        totalDurationRef.current += (now - startTimeRef.current) / 1000;
        startTimeRef.current = null;
      }
      setFinalScore(score);

      // Personal Best check — logged-in user's own record (localStorage per user_id + server).
      // Guests have no user_id so we skip local best tracking.
      const prevBest = isGuest ? 0 : readLocalBest(user);
      const newBest = !isGuest && score > prevBest;
      if (newBest) {
        writeLocalBest(user, score);
        setHighScore(score);
        setIsNewHighScore(true);
        // Small delay so crash lands first
        setTimeout(() => sounds.play("cheer"), 400);
      }

      // Permanently hide 'How to Play' after first game-over
      if (!localStorage.getItem(LS_HOWTO)) {
        localStorage.setItem(LS_HOWTO, "1");
        setShowHowToPlay(false);
      }

      setGameOver(true);

      // Guests do NOT submit to /api/scores here — the game-over popup
      // collects a username and calls /api/auth/guest, which creates the
      // user, session, and score in one atomic backend hop.
      if (isGuest) {
        return;
      }

      // Anti-cheat: sign the score with HMAC + timestamps + server nonce + obstacles.
      const session = runSessionRef.current;
      const clientEnd = Date.now();
      const events = canvasRef.current?.getEvents ? canvasRef.current.getEvents() : [];
      const obstaclesPassed = canvasRef.current?.obstaclesPassed
        ? canvasRef.current.obstaclesPassed()
        : 0;
      const body = {
        score,
        duration_seconds: Math.round(totalDurationRef.current),
        events,
        obstacles_passed: obstaclesPassed,
      };
      if (session?.nonce) {
        try {
          const hash = await signScore({
            score,
            startTime: session.clientStart,
            endTime: clientEnd,
            nonce: session.nonce,
            obstaclesPassed,
          });
          body.start_time = session.clientStart;
          body.end_time = clientEnd;
          body.nonce = session.nonce;
          body.hash = hash;
          const rc = await getRecaptchaToken("submit_score");
          if (rc) body.recaptcha_token = rc;
        } catch {}
      }
      try {
        const res = await api.post("/scores", body);
        // Server confirms Personal Best (upsert-if-higher; never duplicates).
        const pb = res.data?.personal_best;
        if (typeof pb === "number" && pb > readLocalBest(user)) {
          writeLocalBest(user, pb);
          setHighScore(pb);
        }
        fetchRank();
      } catch (e) {
        // Server rejected (tamper detected). Roll back the optimistic PB.
        if (newBest) {
          writeLocalBest(user, prevBest);
          setHighScore(prevBest);
          setIsNewHighScore(false);
        }
        const detail = e?.response?.data?.detail;
        if (detail) {
          toast.error(`Score rejected: ${detail}`, {
            style: { background: "#0B0C10", border: "1px solid #FF007F", color: "#FF007F" },
          });
        }
      } finally {
        // Nonce is single-use; clear it so a stray retry can't reuse it.
        runSessionRef.current = null;
      }
    },
    [fetchRank, user, isGuest]
  );

  // NO in-game popups — sounds only, so gameplay never gets interrupted.
  // Tier crossings show a brief on-screen badge; everything else is audio-only.
  const [tierBadge, setTierBadge] = useState(null); // { idx, label } | null
  const tierTimerRef = useRef(null);
  const handleScoreMilestone = useCallback((type, valueOrIdx, tierLabel) => {
    if (type === "milestone") {
      sounds.play("milestone");
    } else if (type === "rain") {
      // Thunderstorm sfx auto-stops at the exact RAIN_DURATION_MS window
      // (managed inside sounds.playRain) so overlapping rain triggers can
      // never leak audio past the visual overlay.
      sounds.playRain(7000);
    } else if (type === "tier") {
      // Difficulty tier crossed — brief callout badge + louder milestone SFX
      sounds.play("milestone");
      setTierBadge({ idx: valueOrIdx, label: tierLabel });
      if (tierTimerRef.current) clearTimeout(tierTimerRef.current);
      tierTimerRef.current = setTimeout(() => setTierBadge(null), 1600);
    }
  }, []);

  const handleResume = () => {
    if (resumesUsed >= MAX_RESUMES) return;
    setResumesUsed((r) => r + 1);
    setGameOver(false);
    startTimeRef.current = performance.now();
    sounds.playBGM();
    startBgmRateWatcher();
    setTimeout(() => canvasRef.current?.resume(), 30);
    // Log the completed ad view for admin impression tracking.
    // Fire-and-forget — never block the game on this.
    // Pro users don't see ads, so nothing to log.
    if (!user?.is_pro) {
      api.post("/game/ad-view", { context: "game_resume" }).catch(() => {});
    }
  };

  const handleRetry = () => {
    setGameOver(false);
    setIsNewHighScore(false);
    setResumesUsed(0);
    setLiveScore(0);
    totalDurationRef.current = 0;
    startTimeRef.current = performance.now();
    sounds.setBgmSpeed(BGM_BASE_RATE);
    sounds.playBGM();
    startBgmRateWatcher();
    setTimeout(() => canvasRef.current?.retry(), 30);
  };

  // Close the game-over modal — reveals leaderboard behind; Start Run button re-appears
  const handleCloseGameOver = () => {
    sounds.stopBGM();
    sounds.stopRain();
    stopBgmRateWatcher();
    canvasRef.current?.stop && canvasRef.current.stop();
    setGameOver(false);
    setIsNewHighScore(false);
    setGameStarted(false);
    setResumesUsed(0);
    setLiveScore(0);
  };

  const submitUpi = async () => {
    sounds.play("click");
    try {
      await api.post("/me/upi", { upi: upiInput });
      setUpiSaved(true);
      toast.success("UPI submitted. Payouts by admin.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit UPI");
    }
  };

  const logout = async () => {
    sounds.play("click");
    try {
      await api.post("/auth/logout");
    } catch {}
    navigate("/", { replace: true });
  };

  // Guest signup — called from the GameOverModal when a guest submits a
  // username. Creates the guest identity server-side, sets the session
  // cookie, and posts the just-played score to the leaderboard in one call.
  const registerGuest = async (username) => {
    sounds.play("click");
    const body = {
      username,
      score: finalScore,
      duration_seconds: Math.round(totalDurationRef.current),
      obstacles_passed:
        canvasRef.current?.obstaclesPassed?.() ?? null,
    };
    const res = await api.post("/auth/guest", body);
    // Persist the guest identity so a returning visitor (browser closed,
    // cookie expired, or explicit logout+re-enter) gets THE SAME guest
    // account back instead of a brand-new one each time.
    try {
      localStorage.setItem("dg_guest_user_id", res.data.user.user_id);
      localStorage.setItem("dg_guest_username", res.data.user.name);
    } catch {}
    // Upgrade our client state from anonymous → registered guest.
    setUser({ ...res.data.user, is_pro: false });
    setIsGuest(false);
    // Sync personal best + leaderboard rank
    if (finalScore > 0) {
      writeLocalBest(res.data.user, finalScore);
      setHighScore(finalScore);
      setIsNewHighScore(true);
    }
    fetchRank();
    toast.success("Saved to leaderboard!");
    return res.data;
  };

  // Guests can also switch to Google login mid-flow from the game-over popup.
  const handleGoogleUpgrade = () => {
    sounds.play("click");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const openProfile = () => {
    sounds.play("click");
    setProfileUpi(user?.upi || "");
    setShowProfile(true);
    // Fetch career stats for the VIP profile card. Only meaningful for
    // logged-in users with a user_id (guests can view their upgrade CTA
    // without needing this call). Fires once per open — no polling.
    if (user?.user_id && user?.is_pro && !careerLoading) {
      setCareerLoading(true);
      api
        .get(`/profile/${user.user_id}`)
        .then((r) => setCareerStats(r.data))
        .catch(() => setCareerStats(null))
        .finally(() => setCareerLoading(false));
    }
  };

  // Fetch career stats when VIP profile modal opens (any pathway — bottom-nav
  // click or the location.state.openProfile initial open). Non-VIP users see
  // the upgrade CTA instead of stats, so we only spend the API hop for pros.
  useEffect(() => {
    if (!showProfile) return;
    if (!user?.user_id || !user?.is_pro) return;
    if (careerLoading || careerStats) return;
    setCareerLoading(true);
    api
      .get(`/profile/${user.user_id}`)
      .then((r) => setCareerStats(r.data))
      .catch(() => setCareerStats(null))
      .finally(() => setCareerLoading(false));
  }, [showProfile, user?.user_id, user?.is_pro, careerLoading, careerStats]);

  // Reset the cached career stats each time the modal closes so re-opening
  // triggers a fresh fetch (score/rank could have moved between opens).
  useEffect(() => {
    if (!showProfile) setCareerStats(null);
  }, [showProfile]);

  const saveProfileUpi = async () => {
    const upi = profileUpi.trim();
    if (!upi.includes("@") || upi.length < 5) {
      toast.error("Enter a valid UPI id (e.g. yourname@paytm)");
      return;
    }
    setProfileSaving(true);
    try {
      const res = await api.post("/me/upi", { upi });
      setUser((u) => ({ ...u, upi: res.data.upi }));
      toast.success("UPI updated");
      setShowProfile(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update UPI");
    } finally {
      setProfileSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-[#00F0FF] font-mono-arcade text-xs uppercase tracking-[0.3em] animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative min-h-screen bg-[#050505] ${
        gameStarted && !gameOver ? "is-playing" : ""
      }`}
      data-testid="game-page-root"
    >
      <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />

      {/* Header */}
      <header
        data-testid="game-header"
        className="game-dim relative z-10 flex items-center justify-between px-4 md:px-8 py-4 border-b border-[#00F0FF]/20 bg-[#0B0C10]/60 backdrop-blur"
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-[#EAEB18] flex items-center justify-center text-black font-black">
            D
          </div>
          <div className="font-heading font-black text-base md:text-lg tracking-wider text-white">
            DARE<span className="text-[#EAEB18]">DINO</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {user.picture && (
            <img
              src={user.picture}
              alt=""
              className="w-8 h-8 border border-[#00F0FF]/40 object-cover"
              referrerPolicy="no-referrer"
            />
          )}
          <div className="hidden sm:block text-xs text-white font-mono-arcade" data-testid="user-name">
            {user.name}
          </div>
          {isGuest && (
            <button
              data-testid="header-google-signin"
              onClick={handleGoogleUpgrade}
              className="inline-flex items-center gap-1.5 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 font-heading font-black transition-colors"
              title="Sign in with Google to save your best runs"
            >
              <User size={12} strokeWidth={2.5} /> Sign in
            </button>
          )}
          {isIndia && (inTop3 || myWinCard) && (
            <button
              data-testid="my-win-share-btn"
              onClick={() => {
                sounds.play("click");
                // Prefer LIVE top-3 challenge card; fall back to past win.
                if (inTop3 && prevRankRef.current) {
                  setLiveTopCard((c) => c || { rank: prevRankRef.current, score: dailyBestScore });
                } else if (myWinCard) {
                  setShowWinCard(true);
                }
              }}
              title={inTop3 ? "Share your live top-3 rank" : `Share your ${myWinCard?.dayLabel} #1 win`}
              className="inline-flex items-center gap-1 border border-[#EAEB18] text-[#EAEB18] hover:bg-[#EAEB18] hover:text-black text-[10px] uppercase tracking-[0.15em] px-2 py-1 font-heading font-black"
            >
              <Trophy size={12} strokeWidth={2.5} /> Share Win
            </button>
          )}
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Game column */}
        <div className="lg:col-span-2">
          <div className="mb-3 flex items-center justify-between game-dim" data-testid="game-scorebar">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
              [ Live Arena ]
            </div>
            <div
              data-testid="score-display"
              className="font-mono-arcade text-sm md:text-base font-bold flex items-center gap-3"
            >
              <span className="text-[#94A3B8]">
                HI{" "}
                <span data-testid="hi-score" className="text-[#00F0FF] glow-cyan">
                  {String(highScore).padStart(5, "0")}
                </span>
              </span>
              <span
                data-testid="current-score"
                className="text-[#EAEB18] glow-yellow"
              >
                {String(liveScore).padStart(5, "0")}
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
              Resumes:{" "}
              <span className="text-[#EAEB18]" data-testid="resumes-left">
                {MAX_RESUMES - resumesUsed}
              </span>
            </div>
          </div>

          <div className="relative">
            <GameCanvas
              ref={canvasRef}
              onGameOver={handleGameOver}
              onScoreMilestone={handleScoreMilestone}
            />
            {tierBadge && (
              <div
                data-testid="tier-badge"
                key={tierBadge.idx}
                className="absolute top-4 left-1/2 -translate-x-1/2 z-30 pointer-events-none tier-badge-anim"
              >
                <div className="border-2 border-[#EAEB18] bg-[#0B0C10]/95 px-5 py-2 shadow-[4px_4px_0_#FF007F]">
                  <div className="text-[9px] uppercase tracking-[0.35em] text-[#94A3B8]">
                    Tier {tierBadge.idx + 1} · Speed Up
                  </div>
                  <div className="font-heading font-black text-lg text-[#EAEB18] glow-yellow tracking-widest">
                    {tierBadge.label}
                  </div>
                </div>
              </div>
            )}
            {!gameStarted && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                <button
                  data-testid="start-game-btn"
                  onClick={startGame}
                  className="btn-brutal start-run-cta rounded-none flex items-center gap-3"
                >
                  <Play size={24} strokeWidth={3} />
                  START RUN
                </button>
              </div>
            )}
            {gameOver && (
              <GameOverModal
                score={finalScore}
                resumesLeft={MAX_RESUMES - resumesUsed}
                isNewHighScore={isNewHighScore}
                isPro={!!user?.is_pro}
                isGuest={isGuest}
                onGuestRegister={registerGuest}
                onGoogleLogin={handleGoogleUpgrade}
                onResume={handleResume}
                onRetry={handleRetry}
                onClose={handleCloseGameOver}
              />
            )}
          </div>

          {/* UPI panel — India-only (cash payouts). Show only when top-3 AND haven't submitted yet */}
          {isIndia && inTop3 && !upiSaved && (
            <div
              data-testid="upi-panel"
              className="game-dim mt-6 border border-[#EAEB18] bg-[#0B0C10] p-5"
            >
              <div className="flex items-center gap-2 mb-3">
                <Award size={18} strokeWidth={2.5} className="text-[#EAEB18]" />
                <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
                  Prize Eligible
                </div>
                <span className="badge-prize">Top 3</span>
              </div>
              <div className="text-xs text-[#94A3B8] mb-4">
                You're in today's top 3. Enter your UPI ID so admin can process your payout.
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-end">
                <div className="flex-1 w-full">
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] block mb-1">
                    UPI ID
                  </label>
                  <input
                    data-testid="upi-input"
                    type="text"
                    value={upiInput}
                    onChange={(e) => setUpiInput(e.target.value)}
                    placeholder="yourname@bank"
                    className="input-terminal"
                    disabled={upiSaved}
                  />
                </div>
                <button
                  data-testid="upi-submit-btn"
                  onClick={submitUpi}
                  disabled={upiSaved || !upiInput}
                  className="btn-brutal rounded-none px-6 py-3 whitespace-nowrap"
                >
                  {upiSaved ? "Submitted" : "Submit UPI"}
                </button>
              </div>
            </div>
          )}

          {/* How to play — first-time only */}
          {showHowToPlay && (
            <div
              data-testid="how-to-play"
              className="game-dim mt-6 border border-[#00F0FF]/20 bg-[#0B0C10] p-5"
            >
              <div className="flex items-center gap-2 mb-3">
                <Zap size={16} strokeWidth={2.5} className="text-[#00F0FF]" />
                <div className="font-heading font-bold text-xs uppercase tracking-widest text-white">
                  How to Play
                </div>
                <span className="ml-2 text-[9px] uppercase tracking-[0.2em] text-[#EAEB18]">
                  · First-time guide
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Jump</div>
                  <div className="mt-1 text-white font-mono-arcade">SPACE / ↑ / TAP</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Cactus</div>
                  <div className="mt-1 text-[#39FF14] font-mono-arcade">Jump over</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Dragon</div>
                  <div className="mt-1 text-[#00F0FF] font-mono-arcade">Jump over</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Leaderboard column */}
        <div className="game-dim lg:col-span-1" data-testid="leaderboard-column">
          <Leaderboard currentUserId={user.user_id} />
        </div>

        {/* SEO content sections — game-first homepage per Approach A. All
            content is fully rendered in the initial DOM so Googlebot indexes
            everything without user interaction. Users scroll past the game
            to see it. Zero cloaking, zero hidden-content ranking penalty. */}
        <HomeSeoSections />
      </main>

      <div className="game-dim" data-testid="bottom-nav-wrapper">
        <BottomNav onProfileClick={() => setShowProfile(true)} />
      </div>

      {/* PROFILE MODAL */}
      {showProfile && (
        <div
          data-testid="profile-modal"
          onClick={() => setShowProfile(false)}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-vip={user?.is_pro ? "1" : "0"}
            className={`relative w-full max-w-md ${
              user?.is_pro
                ? "vip-profile-frame"
                : "border-2 border-[#00F0FF] bg-[#0B0C10]"
            }`}
          >
            <div className={`relative ${user?.is_pro ? "p-6" : "p-6"}`}>
            {user?.is_pro && (
              <div
                data-testid="own-vip-badge"
                className="absolute -top-3 left-6 flex items-center gap-1.5 bg-[#EAEB18] text-black font-heading font-black text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 shadow-[3px_3px_0_#FF007F]"
              >
                <Crown size={12} strokeWidth={3} />
                DAREPASS VIP
              </div>
            )}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <User size={16} strokeWidth={2.5} className={user?.is_pro ? "text-[#EAEB18]" : "text-[#00F0FF]"} />
                <div className="font-heading font-bold text-sm uppercase tracking-widest text-white">
                  Profile
                </div>
              </div>
              <button
                data-testid="profile-modal-close"
                onClick={() => setShowProfile(false)}
                className="text-[#FF007F] hover:text-[#EAEB18]"
                aria-label="Close"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>

            <div className={`flex items-center gap-3 mb-5 pb-5 border-b ${user?.is_pro ? "border-[#EAEB18]/25" : "border-[#00F0FF]/20"}`}>
              {user?.is_pro ? (
                <div className="relative w-14 h-14 shrink-0" data-testid="own-vip-avatar">
                  <div className="absolute inset-0 rounded-full darepass-ring" aria-hidden="true" />
                  <div className="absolute inset-[2px] rounded-full overflow-hidden bg-[#0B0C10]">
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt=""
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#EAEB18]/10 flex items-center justify-center font-heading font-black text-xl text-[#EAEB18]">
                        {(user.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#0B0C10] flex items-center justify-center border border-[#FFD700] shadow-[0_0_6px_rgba(255,215,0,0.85)]">
                    <Crown size={10} strokeWidth={3} className="darepass-crown-icon" />
                  </div>
                  <Sparkles
                    size={12}
                    strokeWidth={3}
                    className="absolute -top-1 -left-1 text-[#EAEB18] animate-pulse"
                  />
                </div>
              ) : (
                user.picture && (
                  <img
                    src={user.picture}
                    alt=""
                    className="w-12 h-12 border border-[#EAEB18]/50 object-cover shrink-0"
                    referrerPolicy="no-referrer"
                  />
                )
              )}
              <div className="min-w-0">
                {user?.is_pro ? (
                  <div className="text-base font-heading font-black flex items-center gap-1.5 vip-name-shimmer" data-testid="own-vip-name">
                    {user.name}
                    <Crown size={14} strokeWidth={2.5} className="text-[#EAEB18] shrink-0" />
                  </div>
                ) : (
                  <div className="text-sm text-white font-mono-arcade truncate">{user.name}</div>
                )}
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] truncate">
                  {user.email}
                </div>
                {user?.is_pro && (
                  <div className="mt-0.5 text-[9px] uppercase tracking-[0.3em] font-black text-[#EAEB18]">
                    {user?.is_lifetime ? "Premium Member · Lifetime" : "Premium Member · Renews Monthly"}
                  </div>
                )}
              </div>
            </div>

            {user?.is_pro && (
              <div data-testid="own-career-stats" className="grid grid-cols-3 gap-2 mb-5">
                <div
                  data-testid="own-stat-all-time-high"
                  className="border border-[#EAEB18]/40 bg-black/40 p-2.5 text-center"
                >
                  <div className="flex items-center justify-center mb-1 text-[#EAEB18] glow-yellow">
                    <Zap size={14} strokeWidth={2.5} />
                  </div>
                  <div className="font-mono-arcade font-bold text-base text-[#EAEB18] glow-yellow">
                    {careerLoading
                      ? "…"
                      : String(careerStats?.all_time_high ?? 0).padStart(5, "0")}
                  </div>
                  <div className="mt-1 text-[8px] uppercase tracking-[0.2em] text-[#94A3B8] leading-tight">
                    All-Time High
                  </div>
                </div>
                <div
                  data-testid="own-stat-room-wins"
                  className="border border-[#EAEB18]/40 bg-black/40 p-2.5 text-center"
                >
                  <div className="flex items-center justify-center mb-1 text-[#EAEB18] glow-yellow">
                    <Swords size={14} strokeWidth={2.5} />
                  </div>
                  <div className="font-mono-arcade font-bold text-base text-[#EAEB18] glow-yellow">
                    {careerLoading ? "…" : String(careerStats?.room_wins ?? 0)}
                  </div>
                  <div className="mt-1 text-[8px] uppercase tracking-[0.2em] text-[#94A3B8] leading-tight">
                    Room Wins
                  </div>
                </div>
                <div
                  data-testid="own-stat-first-finishes"
                  className="border border-[#EAEB18]/40 bg-black/40 p-2.5 text-center"
                >
                  <div className="flex items-center justify-center mb-1 text-[#EAEB18] glow-yellow">
                    <Trophy size={14} strokeWidth={2.5} />
                  </div>
                  <div className="font-mono-arcade font-bold text-base text-[#EAEB18] glow-yellow">
                    {careerLoading
                      ? "…"
                      : String(careerStats?.leaderboard_first_finishes ?? 0)}
                  </div>
                  <div className="mt-1 text-[8px] uppercase tracking-[0.2em] text-[#94A3B8] leading-tight">
                    #1 Finishes
                  </div>
                </div>
              </div>
            )}

            {user?.is_pro && !user?.is_lifetime && user?.darepass_expires_at && (
              <div
                data-testid="own-darepass-expiry"
                className="mb-5 border border-[#EAEB18]/40 bg-[#EAEB18]/5 px-3 py-2 flex items-center justify-between gap-2"
              >
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#EAEB18] font-black mb-0.5">
                    DarePass expires
                  </div>
                  <div className="text-xs text-white font-mono-arcade">
                    {new Date(user.darepass_expires_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  data-testid="own-darepass-renew-btn"
                  onClick={() => { setShowProfile(false); navigate("/pricing"); }}
                  className="text-[9px] uppercase tracking-widest font-black text-black bg-[#EAEB18] px-3 py-1.5 hover:bg-white transition-colors"
                >
                  Renew
                </button>
              </div>
            )}

            {!user?.is_pro && (
              <button
                data-testid="own-upgrade-btn"
                onClick={() => { setShowProfile(false); navigate("/pricing"); }}
                className="w-full mb-5 inline-flex items-center justify-center gap-2 border-2 border-[#EAEB18] bg-[#EAEB18]/10 text-[#EAEB18] hover:bg-[#EAEB18] hover:text-black transition-colors font-heading font-black text-[11px] uppercase tracking-[0.25em] px-4 py-2.5"
              >
                <Crown size={14} strokeWidth={3} />
                Upgrade to DarePass →
              </button>
            )}

            {isIndia && (
            <>
            <label
              htmlFor="profile-upi-input"
              className="block text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] mb-2"
            >
              UPI ID for cash payouts
            </label>
            <input
              id="profile-upi-input"
              data-testid="profile-upi-input"
              type="text"
              value={profileUpi}
              onChange={(e) => setProfileUpi(e.target.value)}
              placeholder="yourname@paytm"
              className={`w-full bg-[#050505] border ${user?.is_pro ? "border-[#EAEB18]/40 focus:border-[#EAEB18]" : "border-[#00F0FF]/40 focus:border-[#EAEB18]"} outline-none text-white font-mono-arcade text-sm px-3 py-2.5 mb-2`}
              autoComplete="off"
              spellCheck="false"
            />
            <div className="text-[10px] text-[#94A3B8] leading-relaxed mb-5">
              Set or update your UPI id anytime. Payouts to daily Top 3 winners
              are sent to this address. Format: <span className="text-[#39FF14]">name@bank</span>.
            </div>

            <div className="flex gap-2">
              <button
                data-testid="profile-upi-save"
                onClick={saveProfileUpi}
                disabled={profileSaving}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#EAEB18] text-black font-heading font-black text-xs uppercase tracking-widest py-3 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={14} strokeWidth={2.5} />
                {profileSaving ? "Saving..." : "Save UPI"}
              </button>
              <button
                data-testid="profile-cancel"
                onClick={() => setShowProfile(false)}
                className="px-4 border border-[#94A3B8]/40 text-[#94A3B8] hover:text-[#FF007F] hover:border-[#FF007F] text-xs uppercase tracking-widest"
              >
                Cancel
              </button>
            </div>
            </>
            )}

            <div className={`mt-6 pt-4 border-t ${user?.is_pro ? "border-[#EAEB18]/15" : "border-[#00F0FF]/15"} flex justify-center`}>
              <button
                data-testid="profile-logout-btn"
                onClick={logout}
                className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#FF007F] text-[10px] uppercase tracking-[0.3em]"
              >
                <LogOut size={14} strokeWidth={2.5} /> Log out
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* WINNER CARD MODAL (user side) — India-only wording */}
      {isIndia && showWinCard && myWinCard && (
        <WinnerCard
          winner={myWinCard.winner}
          date={myWinCard.date}
          dayLabel={myWinCard.dayLabel}
          onClose={() => setShowWinCard(false)}
        />
      )}

      {/* LIVE TOP-3 CHALLENGE CARD (fires on rank-up into top 3) — India-only */}
      {isIndia && liveTopCard && (
        <WinnerCard
          variant="live"
          rank={liveTopCard.rank}
          winner={{
            name: user.name,
            picture: user.picture,
            score: liveTopCard.score,
          }}
          date={new Date().toISOString().slice(0, 10)}
          dayLabel={new Date().toLocaleDateString("en-IN", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
          onClose={() => setLiveTopCard(null)}
        />
      )}

      {/* RANK-DROP POPUP (once per site-open, only if rank got worse since last visit) */}
      {rankDropPopup && (
        <div
          data-testid="rank-drop-popup"
          onClick={() => setRankDropPopup(null)}
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md border-2 border-[#FF007F] bg-[#0B0C10] p-6"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <TrendingDown size={16} strokeWidth={2.5} className="text-[#FF007F]" />
                <div className="font-heading font-black text-sm uppercase tracking-widest text-white">
                  You Slipped Down
                </div>
              </div>
              <button
                data-testid="rank-drop-close"
                onClick={() => setRankDropPopup(null)}
                className="text-[#94A3B8] hover:text-[#EAEB18]"
                aria-label="Close"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>

            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-1">
                  Last visit
                </div>
                <div
                  data-testid="rank-drop-prev"
                  className="font-heading font-black text-3xl text-[#EAEB18]"
                >
                  #{rankDropPopup.previousRank}
                </div>
              </div>
              <TrendingDown
                size={28}
                strokeWidth={2.5}
                className="text-[#FF007F] mt-4"
              />
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-1">
                  Now
                </div>
                <div
                  data-testid="rank-drop-cur"
                  className="font-heading font-black text-3xl text-[#FF007F]"
                >
                  {rankDropPopup.currentRank == null
                    ? "OUT"
                    : `#${rankDropPopup.currentRank}`}
                </div>
              </div>
            </div>

            <p className="text-sm text-[#E2E8F0] text-center leading-relaxed mb-6">
              Someone knocked you down while you were away.
              {rankDropPopup.currentRank == null
                ? " You even fell out of the top 100."
                : ` You're at #${rankDropPopup.currentRank} now.`}{" "}
              <span className="text-[#EAEB18] font-bold">
                Play now and reclaim your spot!
              </span>
            </p>

            <button
              data-testid="rank-drop-play"
              onClick={() => setRankDropPopup(null)}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#EAEB18] text-black font-heading font-black text-sm uppercase tracking-widest py-3 hover:bg-white transition-colors"
            >
              <Play size={16} strokeWidth={2.5} /> Reclaim My Rank
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
